import { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { interval, throttle } from 'rxjs';
import { first, filter } from 'rxjs/operators';

import {
	Page,
	PageHeader,
	PageFooter,
	Nav,
	Icon,
	VideoPlayer,
	LandingCard,
	Tutorial,
	Switch,
	Preferences,
	UnsubscribeCard,
	EpisodeCard,
} from '../../components';
import {
	useGlobalSettings,
	useUiSettings,
} from '../../hooks/useGlobalSettings';
import { usePlayer, usePlayerUpdates } from '../../kaltura-player';

import FadeIn from '../../utils/FadeIn';
import { cls } from '../../utils/cls';
import { gv_analytics } from '../../utils/Analytics';
import {
	storeVideoState,
	store,
	scrollTo,
	getHash,
	exitFullScreen,
} from '../../utils/utils';

// styles imports
import styles from 'components/Player/VideoPlayer.module.scss';
import { PlayerAPL } from './PlayerAPL';

export const PlayerPage = ({ embed }) => {
	const GLOBAL_SETTINGS = useGlobalSettings();
	const {
		video: { id: entryId, name, videoID },
		userData: { userID },
		featuresConfig: { enableAPL },
		past_episodes = [],
		from_shared_link,
	} = GLOBAL_SETTINGS;
	const uiParams = useUiSettings();
	const show_apl = enableAPL && uiParams.show_apl;

	const durationRef = useRef(0);
	const [videoStarted, setVideoStarted] = useState(false);
	const [played, setPlayed] = useState(store(`${videoID}_finished`));
	const [showGreeting, setShowGreeting] = useState(played);
	const [viewedTutorial, setViewedTutorial] = useState(null);
	const [content, setContent] = useState(null);
	const lastPositionReported = useRef(0);
	const [playerId, setPlayerId] = useState(null);
	const { playerTime$, playerState$ } = usePlayerUpdates(playerId);

	const { getPlayerInstance } = usePlayer(playerId);
	useEffect(() => {
		if (!playerId) return;
	}, [getPlayerInstance, playerId]);

	const { t } = useTranslation();
	const {
		show_header,
		defaultCard,
		show_unsub,
		show_preferences,
		show_landing,
		show_footer,
	} = uiParams;

	/* PLAYER SETUP */
	const showPostEpisodeUI = (bool = true) => setPlayed(bool);

	const loadAPL = (section = 'landing') => {
		// console.log('loadAPL called', section);
		setContent(true);
		showPostEpisodeUI();
		scrollTo(section);
	};

	useEffect(() => {
		if (embed)
			document.getElementsByTagName('html')[0].classList.add('embedded');
	}, [embed]);

	useEffect(() => {
		if (!playerId || !getPlayerInstance()) return; // console.log(
		// 		'Missing playerId or playerInstance',
		// 		playerId,
		// 		playerInstance
		// 	);
		// else console.log('Got playerId and playerInstance');

		const showPostEpisodeUI = (bool = true) => setPlayed(bool);

		const stateSubscription = playerState$.subscribe((state) => {
			// console.log('State updated:', state);
			document.body.setAttribute('data-player-state', state);
			document.body.classList[state === 'playing' ? 'add' : 'remove'](
				'playing'
			);
			let ignore = ['idle', 'loading'];
			if (ignore.indexOf(state) === -1)
				gv_analytics.capture('PLAYER_' + state.toUpperCase());
		});

		const firstPlaySubscription = playerState$
			.pipe(first((state) => state === 'playing'))
			.subscribe(() => {
				setVideoStarted(true);
				durationRef.current = getPlayerInstance().duration;
				storeVideoState(videoID, 'started');
				gv_analytics.capture('VIDEO_STARTED');
			});

		const getVideoProgressEvent = () => ({
			GVID: videoID,
			episode_duration: durationRef.current,
			episode_name: name,
			percent: durationRef.current
				? getPlayerInstance().currentTime / getPlayerInstance().duration
				: 0,
			// sessionID,
			totalTimeWatched: getPlayerInstance()?.currentTime,
		});

		// Cast to integers because env vars are strings by default yay
		const heartbeatInterval = parseInt(
			process.env.REACT_APP_PLAYER_ANALYTICS_HEARTBEAT_INTERVAL_MSEC
		); // How often do we want the heartbeat to send? Reducing this will get you more accurate engagement, but at the cost of overhead and posthog events (counting towards billing totals).
		const minEngagementThreshold = parseInt(
			process.env.REACT_APP_MIN_ENGAGEMENT_TIME_FOR_ANALYTICS_MSEC
		); // Don't start counting engagement analytics until a minimum num of seconds in, to give the user a chance to start watching

		const heartbeatSubscription = playerTime$ // This will not trigger unless playerTime$ is changing, so no heartbeat will be sent when paused
			.pipe(
				filter(
					(time) =>
						// Don't log until we reach the minimum engagement threshold, to avoid lowering the stats with people who don't really start watching the content
						time > minEngagementThreshold &&
						// Don't log heartbeats until are further along than before. We also don't care about heartbeats if they restart the video
						time > lastPositionReported.current
				)
			)
			.pipe(throttle(() => interval(heartbeatInterval)))
			.subscribe((d) => {
				// Update the last position reported
				lastPositionReported.current = d;
				// Capture the event
				gv_analytics.capture(
					'VIDEO_PROGRESS_HEARTBEAT',
					getVideoProgressEvent()
				);
			});

		//	WHEN END CREDITS ARE ABOUT TO ROLL
		const almostFinishedSubscription = playerTime$
			.pipe(
				first((currentTime) => {
					const duration = getPlayerInstance().duration;
					return duration
						? currentTime > (duration - 12) * 1000
						: false;
				})
			)
			.subscribe(() => {
				setShowGreeting(false); // Hide "Welcome Back" to show rating UI instead
				storeVideoState(videoID, 'finished');
				if (show_apl) setContent(true);
				showPostEpisodeUI();
				const { _isInBrowserFullscreen, _isElementInFullscreen } =
					getPlayerInstance()?._localPlayer?._fullscreenController;
				if (_isInBrowserFullscreen || _isElementInFullscreen)
					exitFullScreen();

				// capture 100% completion, even as we're simply watching the end credits, it is 100% of content
				gv_analytics.capture('VIDEO_FINISHED', {
					...getVideoProgressEvent(),
					percent: 1,
				});
				lastPositionReported.current = 1;
			});

		const onPageVisibilityChange = () => {
			if (document.visibilityState === 'hidden') {
				// playerRef.current?.pause() // Enable this to automatically pause when the page is hidden
				const { currentTime } = getPlayerInstance();
				const currentTimeMsec = currentTime * 1000;
				if (
					// Use a floor time value to ensure the engagement metric is a 'real' session
					currentTimeMsec > minEngagementThreshold &&
					// Also make sure the current time is greater than the last time reported. Otherwise, it's wasteful.
					currentTimeMsec > lastPositionReported.current
				) {
					// Update last video position we logged
					lastPositionReported.current = currentTimeMsec;
					// Fire a beacon
					gv_analytics.capture(
						'VIDEO_DROP_OFF_BEACON',
						getVideoProgressEvent()
					);
				}
			} else if (document.visibilityState === 'visible') {
				// playerRef.current?.play() // Enable this to automatically play when the page is shown again
			}
		};
		// read https://dev.to/amersikira/top-3-ways-to-easily-detect-when-a-user-leaves-a-page-using-javascript-2ce4
		document.addEventListener('visibilitychange', onPageVisibilityChange);
		setViewedTutorial(store(process.env.REACT_APP_GV_LATEST_TUTORIAL_CODE));

		const finishedSubscription = playerTime$
			.pipe(
				first((currentTime) => {
					const duration = getPlayerInstance().duration;
					return duration
						? currentTime >= (duration - 7) * 1000
						: false;
				})
			)
			.subscribe(() => {
				setTimeout(() => scrollTo('landing'), 800);
			});

		const shortcuts = (e) => {
			if (e.code === 'Backspace') showPostEpisodeUI(false);
			if (e.code === 'KeyR') {
				localStorage.clear();
			}
			if (e.code === 'End') {
				e.preventDefault();
				setContent(true);

				showPostEpisodeUI();
				scrollTo('landing');
			}
		};
		document.addEventListener('keydown', shortcuts);

		// Don't add double click action to the header unless it exists
		if (!embed && show_header) {
			document
				.querySelector('[data-name=PlayerPageHeader]')
				?.addEventListener('dblclick', function (e) {
					e.preventDefault();
					setContent(true);

					showPostEpisodeUI();
					scrollTo('landing');
				});
		}

		// Don't add double click action to the header unless it exists
		if (!embed && show_footer) {
			document
				.querySelector('[data-name=PlayerPageFooter]')
				.addEventListener('dblclick', function (e) {
					e.preventDefault();
					document.location.hash = '#past_episodes';
				});
		}

		// Cleanup subscriptions
		return () => {
			stateSubscription.unsubscribe();
			firstPlaySubscription.unsubscribe();
			almostFinishedSubscription.unsubscribe();
			heartbeatSubscription.unsubscribe();
			finishedSubscription.unsubscribe();
			document.removeEventListener('keydown', shortcuts);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		show_apl,
		playerId,
		playerState$,
		playerTime$,
		show_footer,
		show_header,
		videoID,
		playerId,
	]);
	// duration is excluded because it's initialized with null until the video is buffered

	const switchChildren = [
		'player',
		'tutorial',
		'preferences',
		'unsubscribe',
		'past_episodes',
	];
	// Try to find the suggested default card in the list. If it doesn't exist, load the first in the list.
	const defaultCardIndex =
		switchChildren.indexOf(defaultCard) !== -1
			? switchChildren.indexOf(defaultCard)
			: switchChildren.indexOf(switchChildren[0]);
	const [switchIndex, setSwitchIndex] = useState([defaultCardIndex]);

	const [videoWasPlayingBeforeSwitch, setVideoWasPlayingBeforeSwitch] =
		useState(false);

	/**
	 *
	 * @param {string} card Name of the card to which you want to switch
	 * @returns
	 */
	const switchToCard = (card) => {
		if (typeof switchChildren[switchChildren.indexOf(card)] === 'undefined')
			return; // Don't run on invalid states
		document.location.hash = '#' + card;

		if (switchIndex[0] !== switchChildren.indexOf(card))
			// Don't log USER_HIDES_* previous card if it's the same card as the new one
			gv_analytics.capture(
				`USER_HIDES_${switchChildren[switchIndex[0]].toUpperCase()}`
			); // Use index that hasn't updated yet to recall the previous card and log that the user hides it
		gv_analytics.capture(
			`USER_SHOWS_${switchChildren[
				switchChildren.indexOf(card)
			].toUpperCase()}`
		); // Use index that hasn't updated yet to recall the previous card and log that the user hides it
		setSwitchIndex([switchChildren.indexOf(card), ...switchIndex]);

		if (videoStarted && !getPlayerInstance()?.paused)
			setVideoWasPlayingBeforeSwitch(true);
		else setVideoWasPlayingBeforeSwitch(false);

		window.scrollTo({
			top: 0,
			behavior: 'smooth',
			/* you can also use 'auto' behaviour in place of 'smooth' */
		});
		videoWasPlayingBeforeSwitch && card === 'player'
			? getPlayerInstance()?.play()
			: getPlayerInstance()?.pause();
		if (card === 'tutorial')
			store(process.env.REACT_APP_GV_LATEST_TUTORIAL_CODE, true);
	};

	// useEffect(
	// 	() => console.log('videoWasPlaying', videoWasPlayingBeforeSwitch),
	// 	[videoWasPlayingBeforeSwitch]
	// );

	useMemo(() => {
		// WINDOW.ONHASHCHANGE
		// Without this, clicking a link to a new hash location that opens in an existing window will not navigate the user.
		// this is because the browser recognizes that the link is the 'same' and just updates the URL (including the new hash location) in the existing window.
		// However, this does not reload the page, and thus does not trigger a re-render.
		// Monitoring onhashchange means that when clicking the tutorial or preferences link in an email AFTER clicking the main play CTA WILL navigate the user to the desired card
		let destination = () => getHash();
		window.addEventListener('hashchange', (e) =>
			switchToCard(destination())
		);
		if (window.location.pathname.slice(0, 4) === '/apl')
			loadAPL(destination());
		else switchToCard(destination());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const GenerateVideoPlayer = () => (
		<section id="player">
			{/* VIDEO PLAYER and Voting Interface  */}
			<VideoPlayer
				active={switchChildren[switchIndex[0]] === 'player'}
				DOM_ID="player"
				id={entryId}
				playerId={playerId}
				setPlayerId={setPlayerId}
				playerInstance={playerId && getPlayerInstance()}
				muted={playerId && getPlayerInstance()?.muted}
			/>
		</section>
	);
	return embed ? (
		GenerateVideoPlayer()
	) : (
		<Page>
			{/* NAV SHOWN AFTER VIDEO PLAYED */}
			{played && (
				// Always display the 'Home' button
				<Nav>
					<a href="#landing">
						<Icon name="home" />
						<span>{t('home_')}</span>
					</a>
					{/* Dynamically display other nav buttons depending on what Aller Plus Loin content exists */}
					{content?.length
						? content.map(({ props: { id } }) => (
								<a key={id} href={`#${id}`}>
									<Icon name={id} />
									<span>{t(`${id}_`)}</span>
								</a>
						  ))
						: null}
				</Nav>
			)}
			<PageHeader>
				<h1 data-name="header-title">
					{document.location.hash === '#past_episodes'
						? process.env.REACT_APP_BRAND_NAME
						: name}
				</h1>
				<FadeIn
					delayInSeconds={1.5}
					style={{
						margin: 'auto 20px auto auto',
						cursor: 'pointer',
					}}
				>
					{
						// Prevent the user from navigating if he is not on the player
						<Icon
							/* Tutorial toggle button */
							name="info"
							size={24}
							className={cls(
								!viewedTutorial ? styles.PulseSlowly : null,
								styles.TutorialIcon
							)}
							onClick={() => (window.location.hash = 'tutorial')}
							data-name="ControlTutorial"
						/>
					}
				</FadeIn>
			</PageHeader>

			{/* VIDEO PLAYER AND TUTORIAL W/ FLIP EFFECT */}
			<Switch
				className={styles.EntireContainer}
				active={switchIndex[0]}
				data-name="PlayerSwitchCard"
			>
				{/* VIDEO PLAYER and Voting Interface  */}
				{GenerateVideoPlayer()}
				<Tutorial
					active={switchChildren[switchIndex[0]] === 'tutorial'}
					toggleTutorial={() =>
						(window.location.hash =
							switchChildren[switchIndex[0]] === 'tutorial'
								? 'player'
								: 'tutorial')
					}
					id={'unsubscribe'}
					data-name="Tutorial"
				/>
				<Preferences back={() => (window.location.hash = 'player')} />
				<UnsubscribeCard
					active={switchChildren[switchIndex[0]] === 'unsubscribe'}
					userID={userID}
					back={() => (window.location.hash = 'player')}
				/>
				<EpisodeCard
					id={'past_episodes'}
					active={switchChildren[switchIndex[0]] === 'past_episodes'}
					back={() => (window.location.hash = 'player')}
					tiles={past_episodes.map((ep) => {
						return { type: 'EpisodeCard', props: ep };
					})}
				/>
			</Switch>
			{/* CONTENT CARDS SHOWN AFTER VIDEO */}
			{show_landing && played && (
				<LandingCard
					id="landing"
					playerId={playerId}
					next={content?.[0]?.props?.id}
					showWelcomeBack={showGreeting}
					pastEpisodeCount={past_episodes?.length}
					isSmallDevice={window.matchMedia('(max-width: 540px)')}
					GLOBAL_SETTINGS={GLOBAL_SETTINGS}
					UI_SETTINGS={uiParams}
				/>
			)}
			{show_apl && played && (
				<PlayerAPL content={content} setContent={setContent} />
			)}
			<PageFooter
				cta={() =>
					window.open(
						`https://www.visigo.fr?utm_source=${encodeURI(
							process.env.REACT_APP_BRAND_NAME
						)}&utm_medium=player&utm_campaign=footer&utm_content=${name}`
					)
				}
			>
				{/* <Link to="/preferences">{t('preferences_link')}</Link> Disabled for now */}
				{/* Only display 'unsubscribe' link if not from shared link, and if url params contain 'unsub' (for unsubscribe) */}
				{!from_shared_link && userID > -1 ? (
					<>
						{show_preferences && (
							<div
								onClick={() => {
									window.location.hash = 'preferences';
								}}
								data-name="PreferencesLink"
							>
								<strong>{t('edit_preferences_cta')}</strong>
							</div>
						)}
						{show_unsub && (
							<div
								onClick={() =>
									(window.location.hash = 'unsubscribe')
								}
								data-name="UnsubscribeLink"
							>
								<strong>{t('unsub_cta')}</strong>
							</div>
						)}
					</>
				) : null}
			</PageFooter>
		</Page>
	);
};
